import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, fmtUSD, safe } from "@/lib/api";
import { CanvasTabs } from "@/components/meridian/CanvasTabs";
import { ActivityLog } from "@/components/meridian/ActivityLog";
import { WireDashboard } from "./WireDashboard";
import { WireTable } from "./WireTable";
import { AlertsPanel, AlertDrawer } from "./AlertsPanel";

const RANGE_LABEL = { today: "Last 24h", last_7_days: "Last 7 days", last_30_days: "Last 30 days" };
const list = (v) => (Array.isArray(v) ? v.join(", ") : String(v));
const CHIP = {
  min_amount: (v) => `Amount ≥ ${fmtUSD(v, true)}`, max_amount: (v) => `Amount ≤ ${fmtUSD(v, true)}`, banks: (v) => `Bank: ${list(v)}`,
  statuses: (v) => `Status: ${list(v)}`, aml_review: () => "Needs AML review", date_range: (v) => RANGE_LABEL[v] || v,
  direction: (v) => `Direction: ${v}`, countries: (v) => `Country: ${list(v)}`, counterparty: (v) => `Counterparty ~ ${v}`,
};
const chipText = (k, v) => (CHIP[k] ? CHIP[k](v) : `${k}: ${list(v)}`);

export function FilterChips({ filter, onRemove, onClear }) {
  const keys = Object.keys(filter || {});
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="filter-chips">
      <span className="eyebrow">Filter</span>
      {!keys.length && <span className="text-xs text-slate-500">All wires (30 days)</span>}
      {keys.map((k) => (
        <span key={k} data-testid={`filter-chip-${k}`} className="rise flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded border border-sky-500/40 bg-sky-600/10 text-sky-800">
          {chipText(k, filter[k])}
          <button data-testid={`filter-chip-remove-${k}`} onClick={() => onRemove(k)} className="hover:text-slate-900"><X className="w-3 h-3" /></button>
        </span>
      ))}
      {keys.length > 0 && <button data-testid="filter-clear-all" onClick={onClear} className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2">Clear</button>}
    </div>
  );
}

export default function WireCanvas({ task, tab, setTab, reload, openAlert, setOpenAlert, version }) {
  const [q, setQ] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const filter = task.filter || {};
  const fkey = JSON.stringify(filter);
  useEffect(() => { api.post("/wires/query", { filter: JSON.parse(fkey) }).then((r) => setQ(r.data)); }, [fkey, version]);
  const loadAlerts = useCallback(() => api.get("/alerts").then((r) => setAlerts(r.data)), []);
  useEffect(() => { loadAlerts(); }, [loadAlerts, version]);
  const setFilter = safe(async (f) => { await api.put(`/tasks/${task.id}/filter`, { filter: f }); reload(); });
  const remove = (k) => { const f = { ...filter }; delete f[k]; setFilter(f); };
  const alert = alerts.find((a) => a.id === openAlert);
  const notes = alerts.filter((a) => a.note);
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="wire-canvas">
      <div className="px-6 py-4 border-b border-slate-200 space-y-3">
        <div className="flex justify-between items-end">
          <div><div className="eyebrow">Wire Operations · Live feed</div><h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{task.title}</h1></div>
          {q && <span className="num text-[11px] text-slate-500" data-testid="wire-universe">{q.kpis.count} of {q.kpis.universe} wires</span>}
        </div>
        <FilterChips filter={filter} onRemove={remove} onClear={() => setFilter({})} />
      </div>
      <CanvasTabs tab={tab} setTab={setTab} tabs={[["dashboard", "Dashboard"], ["transactions", "Transactions", q?.rows.length], ["alerts", "AML alerts", alerts.filter((a) => a.status === "Open").length], ["draft", "Drafts", notes.length], ["activity", "Activity"]]} />
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
        {!q ? <div className="p-6 text-sm text-slate-500">Loading wires…</div> : (
          <>
            {tab === "dashboard" && <WireDashboard q={q} onOpenAlert={setOpenAlert} onBank={(b) => setFilter({ ...filter, banks: [b] })} />}
            {tab === "transactions" && <div className="p-6"><WireTable rows={q.rows} onOpenAlert={setOpenAlert} source={q.source} /></div>}
          </>
        )}
        {tab === "alerts" && <AlertsPanel alerts={alerts} onOpen={setOpenAlert} />}
        {tab === "draft" && <AlertsPanel alerts={notes} onOpen={setOpenAlert} notesMode />}
        {tab === "activity" && <ActivityLog taskId={task.id} version={version} />}
      </div>
      <AlertDrawer alert={alert} onClose={() => setOpenAlert(null)} onChanged={loadAlerts} />
    </div>
  );
}
